/**
 * The rollout panel's data model — pure, so the panel's arithmetic can be
 * tested without a render (LOCKER-408).
 *
 * Access precedence itself is NOT defined here: it comes from
 * `shared/featureResolution`, the same module the backend resolver calls. This
 * file only shapes rows, counts them, filters them, and works out what a bulk
 * action would actually change.
 */
import {
  FeatureAccessState,
  isFeatureAccessGranted,
  resolveFeatureAccessState,
} from "shared/featureResolution"
import { FeatureAssignment } from "shared/featureAssignment"
import { Tenant } from "shared/tenant"

/** One organization's row on the rollout panel. */
export type FeatureRolloutRow = {
  tenantId: string
  /** Display name, falling back to the id so a nameless tenant is still identifiable. */
  name: string
  description?: string
  /** The tenant's assignment for this feature, if one exists. */
  assignment?: FeatureAssignment
  state: FeatureAccessState
  /** Whether the tenant can use the feature — the resolved verdict. */
  granted: boolean
  /** When the assignment last changed. Undefined when there is no assignment. */
  changedAt?: string
}

/** Which rows the panel is showing. */
export type FeatureRolloutFilter = "all" | "on" | "off" | "unassigned"

/** A bulk action's direction. */
export type FeatureRolloutAction = "activate" | "deactivate"

export type FeatureRolloutCounts = {
  total: number
  /** Granted by an explicit activated assignment. */
  activatedByAssignment: number
  /** Granted by the feature default, with no assignment of their own. */
  activatedByDefault: number
  /** Not granted, by explicit assignment. */
  deactivatedByAssignment: number
  /** Not granted, and nobody has decided — no assignment, default off. */
  unassigned: number
  /** Everyone the feature is live for. */
  granted: number
}

/**
 * Index assignments by tenant.
 *
 * Later rows win on a duplicate. A tenant should hold at most one assignment
 * per feature, but nothing enforces that, and picking the last read is at least
 * deterministic — silently resolving from an arbitrary one is how a row shows a
 * state no write produced.
 */
export const indexAssignmentsByTenant = (
  assignments: FeatureAssignment[] | undefined,
): Record<string, FeatureAssignment> => {
  const map: Record<string, FeatureAssignment> = {}
  ;(assignments || []).forEach((a) => {
    if (a?.tenantId) map[a.tenantId] = a
  })
  return map
}

/**
 * Build one row per tenant — every organization, not only the ones holding an
 * assignment. A rollout surface that lists only assignments cannot show you the
 * organizations nobody has made a call on yet, which is most of them.
 */
export const buildFeatureRolloutRows = (
  tenants: Tenant[] | undefined,
  assignmentsByTenant: Record<string, FeatureAssignment>,
  defaultActivated: boolean,
): FeatureRolloutRow[] =>
  (tenants || [])
    .filter((t: any) => !!t?.id)
    .map((t: any) => {
      const assignment = assignmentsByTenant[t.id]
      const state = resolveFeatureAccessState(assignment, defaultActivated)
      return {
        tenantId: t.id,
        name: t.name || t.id,
        description: t.description,
        assignment,
        state,
        granted: isFeatureAccessGranted(state),
        changedAt: assignment
          ? (assignment as any).updatedAt || (assignment as any).createdAt
          : undefined,
      }
    })

export const countFeatureRollout = (
  rows: FeatureRolloutRow[],
): FeatureRolloutCounts => {
  const counts: FeatureRolloutCounts = {
    total: rows.length,
    activatedByAssignment: 0,
    activatedByDefault: 0,
    deactivatedByAssignment: 0,
    unassigned: 0,
    granted: 0,
  }
  rows.forEach((r) => {
    if (r.granted) counts.granted++
    switch (r.state) {
      case FeatureAccessState.ActivatedByAssignment:
        counts.activatedByAssignment++
        break
      case FeatureAccessState.ActivatedByDefault:
        counts.activatedByDefault++
        break
      case FeatureAccessState.DeactivatedByAssignment:
        counts.deactivatedByAssignment++
        break
      case FeatureAccessState.Unassigned:
        counts.unassigned++
        break
    }
  })
  return counts
}

const matchesFilter = (
  row: FeatureRolloutRow,
  filter: FeatureRolloutFilter,
): boolean => {
  switch (filter) {
    case "on":
      return row.granted
    case "off":
      return !row.granted
    case "unassigned":
      return row.state === FeatureAccessState.Unassigned
    case "all":
    default:
      return true
  }
}

/**
 * Narrow rows by the search box and the state chip.
 *
 * Search is the same three fields the page searched before (name, description,
 * id) so muscle memory carries over. It NARROWS WHAT IS DISPLAYED ONLY — bulk
 * actions read the explicit selection, never this (LOCKER-408 DoD5).
 */
export const filterFeatureRolloutRows = (
  rows: FeatureRolloutRow[],
  search: string,
  filter: FeatureRolloutFilter,
): FeatureRolloutRow[] => {
  const s = (search || "").trim().toLowerCase()
  return rows.filter((r) => {
    if (!matchesFilter(r, filter)) return false
    if (!s) return true
    return (
      r.name.toLowerCase().includes(s) ||
      (r.description || "").toLowerCase().includes(s) ||
      r.tenantId.toLowerCase().includes(s)
    )
  })
}

/** What flipping one row would write. */
export const nextActionFor = (row: FeatureRolloutRow): FeatureRolloutAction =>
  row.granted ? "deactivate" : "activate"

/**
 * Would this action change this row's stored assignment?
 *
 * Deliberately about the ASSIGNMENT, not about access. A row granted only by
 * the feature default has no assignment, so "activate" does change it — it
 * writes an explicit activation, pinning access that until now depended on a
 * flag someone else could flip. Reading it as a no-op because access already
 * held would silently skip the write the operator asked for.
 */
export const wouldChange = (
  row: FeatureRolloutRow,
  action: FeatureRolloutAction,
): boolean => {
  const target = action === "activate"
  return row.assignment?.isActivated !== target
}

export type FeatureRolloutBulkPlan = {
  action: FeatureRolloutAction
  /** Rows whose assignment this action writes. */
  changing: FeatureRolloutRow[]
  /** Selected rows already in the target state — nothing is written for them. */
  unchanged: FeatureRolloutRow[]
}

/**
 * Work out what a bulk action would do to an explicit selection, splitting the
 * rows it writes from the ones already in the target state.
 *
 * The split is the point: the old confirmation quoted one number derived from
 * the search box, so an operator could not tell whether "42 organizations" meant
 * 42 writes or 3 writes and 39 rows that happened to match the filter
 * (LOCKER-408 DoD5).
 */
export const planFeatureRolloutBulk = (
  rows: FeatureRolloutRow[],
  selectedTenantIds: Iterable<string>,
  action: FeatureRolloutAction,
): FeatureRolloutBulkPlan => {
  const selected = new Set(selectedTenantIds)
  const inSelection = rows.filter((r) => selected.has(r.tenantId))
  return {
    action,
    changing: inSelection.filter((r) => wouldChange(r, action)),
    unchanged: inSelection.filter((r) => !wouldChange(r, action)),
  }
}
